import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mantine/core';
import { CircleNotch, WarningCircle, CheckCircle, PencilSimple, FloppyDisk, X, GoogleLogo, MetaLogo, Stack as StackIcon } from '@phosphor-icons/react';
import api from '../utils/api';
import { getGoogleAccounts } from '../utils/googleAccounts';

const ALL = '__all__';

/**
 * Account picker for a single platform (Google or Meta). The chosen account (or
 * "all accounts") becomes that platform's scope and re-scopes the whole app.
 */
export default function SelectAccountModal({ platform = 'google_ads', onClose, onSelected, onRenamed }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);
  const [currentId, setCurrentId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [checked, setChecked] = useState([]);
  const [applying, setApplying] = useState(false);

  const isMeta = platform === 'meta_ads';
  const platformLabel = isMeta ? 'Meta Ads' : 'Google Ads';

  useEffect(() => {
    getGoogleAccounts()
      .then(res => {
        const accts = (res.data.accounts || [])
          .filter(a => (a.platform || 'google_ads') === platform)
          .sort((a, b) => (b.campaign_count || 0) - (a.campaign_count || 0));
        setAccounts(accts);
        const cur = (isMeta ? res.data.current_meta_account_id : res.data.current_google_account_id) || '';
        setCurrentId(cur);
        if (cur && cur !== ALL) setChecked(cur.split(',').filter(Boolean));
      })
      .catch(() => setError(t('shell.selectAccount.errorLoad')))
      .finally(() => setLoading(false));
  }, [platform, isMeta]);

  const handleSelect = async (account) => {
    if (editingId) return;
    setSelecting(account.id);
    try {
      const res = await api.post('/accounts/select-ad-account/', { platform, account_id: account.id, manager_id: account.manager_id || '' });
      onSelected(account, res.data.syncing);
    } catch {
      setError(t('shell.selectAccount.errorSelect'));
      setSelecting(null);
    }
  };

  const toggleChecked = (e, account) => {
    e.stopPropagation();
    setChecked(prev => prev.includes(account.id)
      ? prev.filter(id => id !== account.id)
      : [...prev, account.id]);
  };

  const handleApplyMany = async () => {
    setApplying(true);
    try {
      const picked = accounts.filter(a => checked.includes(a.id));
      const res = await api.post('/accounts/select-ad-account/', {
        platform,
        account_ids: picked.map(a => ({ id: a.id, manager_id: a.manager_id || '' })),
      });
      onSelected(picked[0], res.data.syncing);
    } catch {
      setError(t('shell.selectAccount.errorSaveMany'));
      setApplying(false);
    }
  };

  const displayName = (account) => account.custom_name || account.name || t('shell.selectAccount.accountFallback', { id: account.id });

  const handleStartRename = (e, account) => {
    e.stopPropagation();
    setEditingId(account.id);
    setEditName(account.custom_name || account.name || '');
  };

  const handleSaveRename = async (accountId) => {
    if (!editName.trim()) { setEditingId(null); return; }
    setSavingName(true);
    try {
      await api.post('/accounts/rename-google-ads-account/', { account_id: accountId, name: editName.trim() });
      setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, custom_name: editName.trim() } : a));
      if (onRenamed) onRenamed(accountId, editName.trim());
      setEditingId(null);
    } catch {}
    setSavingName(false);
  };

  const PlatformLogo = isMeta ? MetaLogo : GoogleLogo;
  const totals = accounts.reduce((acc, a) => ({
    campaigns: acc.campaigns + (a.campaign_count || 0),
    managed: acc.managed + (a.managed_campaign_count || 0),
  }), { campaigns: 0, managed: 0 });

  return (
    <Modal opened onClose={onClose} centered withCloseButton={false} size="md" overlayProps={{ backgroundOpacity: 0.3, blur: 4 }}>
      <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('shell.selectAccount.title', { platform: platformLabel })}</h2>
      <p className="text-xs text-gray-500 mb-4">
        {t('shell.selectAccount.subtitle')}
      </p>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <CircleNotch className="w-5 h-5 animate-spin text-indigo-600" />
          <span className="ml-2 text-sm text-gray-500">{t('shell.selectAccount.loading')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-3">
          <WarningCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && accounts.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-gray-700 mb-1">{t('shell.selectAccount.noneTitle', { platform: platformLabel })}</p>
          <p className="text-xs text-gray-500">{t('shell.selectAccount.noneSubtitle', { platform: platformLabel })}</p>
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {/* All accounts option */}
          <div
            onClick={() => handleSelect({ id: ALL, platform })}
            className={`w-full text-left px-3 py-2.5 rounded-md border transition ${
              currentId === ALL ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/50'
            } ${selecting ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
          >
            <div className="flex items-center gap-2">
              <StackIcon className="w-4 h-4 text-indigo-500 shrink-0" weight="bold" />
              <p className="text-sm font-medium text-gray-900">{t('shell.selectAccount.allAccounts', { platform: platformLabel })}</p>
              {currentId === ALL && <CheckCircle className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
              {selecting === ALL && <CircleNotch className="w-4 h-4 animate-spin text-indigo-600 shrink-0 ml-auto" />}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 pl-6">
              {t('shell.selectAccount.allCampaignsSummary', { count: totals.campaigns, managed: totals.managed })}
            </div>
          </div>

          {accounts.map(account => {
            const isCurrent = account.id === currentId || currentId.split(',').includes(account.id);
            const isEditing = editingId === account.id;
            return (
              <div
                key={account.id}
                onClick={() => !isEditing && handleSelect(account)}
                className={`w-full text-left px-3 py-2.5 rounded-md border transition ${
                  isCurrent
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/50'
                } ${selecting ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveRename(account.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                          className="flex-1 text-sm px-2 py-1 border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          placeholder={t('shell.selectAccount.namePlaceholder')}
                        />
                        <button onClick={() => handleSaveRename(account.id)} disabled={savingName} className="p-1 text-indigo-600 hover:text-indigo-800">
                          {savingName ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <FloppyDisk className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked.includes(account.id)}
                          onChange={(e) => toggleChecked(e, account)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-3.5 h-3.5 accent-indigo-600 shrink-0 cursor-pointer"
                          title={t('shell.selectAccount.checkboxTitle')}
                        />
                        <PlatformLogo className={`w-4 h-4 shrink-0 ${isMeta ? 'text-[#0866FF]' : 'text-gray-500'}`} weight="bold" />
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {displayName(account)}
                        </p>
                        {isCurrent && (
                          <CheckCircle className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                        )}
                        {!isMeta && (
                          <button onClick={(e) => handleStartRename(e, account)} className="p-0.5 text-gray-300 hover:text-indigo-500 shrink-0" title={t('shell.selectAccount.renameTitle')}>
                            <PencilSimple className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5 pl-6">
                      <span className="text-xs font-mono text-gray-500">{account.id}</span>
                      {account.manager_id && (
                        <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" title={t('shell.selectAccount.mccTitle', { id: account.manager_id })}>
                          MCC {account.manager_id}
                        </span>
                      )}
                      {account.campaign_count > 0 ? (
                        <span className="text-xs text-green-600 font-medium">
                          {t('shell.selectAccount.accountCampaignsSummary', { count: account.campaign_count, managed: account.managed_campaign_count || 0 })}
                        </span>
                      ) : (
                        <span className="text-xs text-orange-500">{t('shell.selectAccount.noCampaigns')}</span>
                      )}
                    </div>
                  </div>
                  {selecting === account.id && (
                    <CircleNotch className="w-4 h-4 animate-spin text-indigo-600 shrink-0" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
        >
          {t('shell.selectAccount.cancel')}
        </button>
        {checked.length >= 2 && (
          <button
            onClick={handleApplyMany}
            disabled={applying}
            className="flex-1 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-60"
          >
            {applying ? t('shell.selectAccount.saving') : t('shell.selectAccount.showData', { count: checked.length })}
          </button>
        )}
      </div>
    </Modal>
  );
}
